import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/use-toast';

interface UseReplyNavigation {
  /** Scroll a message into view + highlight it, recording where to return. */
  jumpToMessage: (messageId: string) => void;
  /** Step back down to the position before the most recent jump. */
  goBack: () => void;
  /** Whether there's a recorded position to return to (drives the button). */
  canGoBack: boolean;
  /** The message currently flashed as the jump target, if any. */
  highlightedId: string | null;
  /** Merge into the scroll container's onScroll to retire stale return points. */
  handleNavScroll: () => void;
}

const HIGHLIGHT_MS = 1600;
const PROGRAMMATIC_SCROLL_MS = 600;
const NEAR_PX = 40;

/**
 * Telegram-style reply navigation. Clicking a reply preview scrolls up to the
 * quoted message (located by its `data-message-id`) and remembers where you
 * were; a floating "back down" button then walks you back down through those
 * positions one step at a time. The stack auto-prunes as you manually scroll
 * back down past a recorded point, so the button disappears once it's no longer
 * relevant (and when you reach the original spot). A target outside the loaded
 * window prompts the user to load older messages instead.
 */
export function useReplyNavigation(
  containerRef: React.RefObject<HTMLDivElement | null>,
): UseReplyNavigation {
  const [returnStack, setReturnStack] = useState<number[]>([]);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  // True while a smooth scroll we initiated is settling, so its scroll events
  // don't get treated as the user manually scrolling.
  const programmaticRef = useRef(false);
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markProgrammatic = useCallback(() => {
    programmaticRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      programmaticRef.current = false;
    }, PROGRAMMATIC_SCROLL_MS);
  }, []);

  const jumpToMessage = useCallback(
    (messageId: string) => {
      const container = containerRef.current;
      const el = container?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
      if (!container || !el) {
        toast({
          description: 'That message isn’t loaded yet — scroll up to load older messages.',
        });
        return;
      }
      setReturnStack((s) => [...s, container.scrollTop]);
      markProgrammatic();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedId(messageId);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
    },
    [containerRef, markProgrammatic],
  );

  const goBack = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    setReturnStack((s) => {
      if (s.length === 0) return s;
      markProgrammatic();
      container.scrollTo({ top: s[s.length - 1], behavior: 'smooth' });
      return s.slice(0, -1);
    });
  }, [containerRef, markProgrammatic]);

  const handleNavScroll = useCallback(() => {
    if (programmaticRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    setReturnStack((s) => {
      if (s.length === 0) return s;
      // Pop the trailing return points the user has now scrolled back down to
      // or past on their own — once none remain, the button hides.
      let i = s.length;
      while (i > 0 && container.scrollTop >= s[i - 1] - NEAR_PX) i--;
      return i === s.length ? s : s.slice(0, i);
    });
  }, [containerRef]);

  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    },
    [],
  );

  return {
    jumpToMessage,
    goBack,
    canGoBack: returnStack.length > 0,
    highlightedId,
    handleNavScroll,
  };
}
